import { z } from 'zod';
import { ProjectJobLinksSchema } from './project-job-links.js';
import { EvidenceCitationSchema, WorkflowManifestSchema, WorkflowDocumentSchema, WorkflowResultSchema, CurrentWorkflowResultSchema, } from './workflow.js';
export const SourceIdSchema = z.string().regex(/^\d{1,24}$/);
const ShortText = z.string().max(2000);
const SourceUrl = z
    .string()
    .url()
    .max(2000)
    .refine((value) => new URL(value).protocol === 'https:', 'Use an HTTPS source URL.');
const Facts = z.array(z.record(z.string(), z.unknown())).max(1000);
export const CoverageSchema = z.object({
    category: z.string().min(1).max(100),
    opening_ref: z.string().max(100).nullable().default(null),
    status: z.enum([
        'captured',
        'reused',
        'not_found',
        'retrieval_failed',
        'not_reviewed',
        'not_applicable',
    ]),
    reason: ShortText,
    source_refs: z.array(z.string().max(200)).max(100).default([]),
});
export const DocumentSchema = z
    .object({
    client_document_id: z.string().min(1).max(100),
    multipart_field: z
        .string()
        .regex(/^[a-zA-Z][a-zA-Z0-9_-]{0,79}$/)
        .optional(),
    retained_document_id: z.string().uuid().optional(),
    document_type: z.string().min(1).max(100),
    workflow: WorkflowDocumentSchema.optional(),
    original_filename: z.string().min(1).max(255),
    capture_method: z.enum([
        'original_download',
        'browser_print',
        'screenshot',
        'structured_export',
    ]),
    source_entity_type: z.string().max(100),
    source_entity_id: SourceIdSchema,
    source_url: SourceUrl,
    source_document_date: z.string().max(100).nullable(),
    source_revision: z.string().max(100).nullable(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    byte_length: z
        .number()
        .int()
        .positive()
        .max(20 * 1024 * 1024),
    mime_type: z.enum([
        'application/pdf',
        'image/png',
        'image/jpeg',
        'text/plain',
        'application/json',
    ]),
    page_count: z.number().int().positive().max(100).nullable(),
})
    .strict()
    .refine((d) => Boolean(d.multipart_field) !== Boolean(d.retained_document_id), 'Provide either a file part or a retained document reference.');
export const ReusedEvidenceSchema = z
    .object({
    evidence_ref: z.string().regex(/^legacy-form:\d+:\d+$/),
    fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    verification: z
        .object({
        checked_at: z.string().datetime({ offset: true }),
        method: z.enum(['source_revision', 'browser_review']),
        source_current: z.literal(true),
        notes: ShortText,
    })
        .strict(),
})
    .strict();
export const ManifestSchema = z
    .object({
    schema_version: z.enum(['1', '2']),
    workflow: WorkflowManifestSchema.optional(),
    client_submission_id: z.string().uuid(),
    batch_id: z.string().uuid().nullable().default(null),
    base_submission_id: z.string().uuid().nullable().default(null),
    project: z
        .object({
        st_project_id: SourceIdSchema,
        display_project_number: SourceIdSchema,
        source_url: SourceUrl,
    })
        .strict(),
    collection: z
        .object({
        started_at: z.string().datetime({ offset: true }),
        finished_at: z.string().datetime({ offset: true }),
    })
        .strict(),
    entities: Facts,
    project_job_links: ProjectJobLinksSchema.optional(),
    openings: Facts,
    observations: Facts,
    documents: z.array(DocumentSchema).max(50),
    reused_evidence: z.array(ReusedEvidenceSchema).max(100).default([]),
    coverage: z.array(CoverageSchema).min(1).max(500),
    gaps: z.array(ShortText).max(500),
})
    .strict()
    .superRefine((m, ctx) => {
    if (m.project_job_links?.some((link) => link.st_project_id !== m.project.st_project_id))
        ctx.addIssue({ code: 'custom', path: ['project_job_links'], message: 'Job relationships must belong to this internal project ID.' });
    if (m.schema_version === '2' && !m.workflow)
        ctx.addIssue({
            code: 'custom',
            message: 'Workflow inventory is required for version 2.',
            path: ['workflow'],
        });
    if (m.workflow) {
        const orders = m.workflow.orders.map((o) => o.order_ref);
        const openings = m.workflow.openings.map((o) => o.opening_ref);
        if (new Set(orders).size !== orders.length ||
            new Set(openings).size !== openings.length)
            ctx.addIssue({
                code: 'custom',
                message: 'Duplicate order or opening reference.',
                path: ['workflow'],
            });
        for (const o of m.workflow.orders) {
            if (new Set(o.lines.map((l) => l.line_ref)).size !== o.lines.length ||
                [...o.opening_refs, ...o.lines.flatMap((l) => l.opening_refs)].some((r) => !openings.includes(r)))
                ctx.addIssue({
                    code: 'custom',
                    message: 'Invalid line/opening reference.',
                    path: ['workflow'],
                });
        }
        for (const d of m.documents)
            if (d.workflow &&
                (d.workflow.order_refs.some((r) => !orders.includes(r)) ||
                    d.workflow.opening_refs.some((r) => !openings.includes(r))))
                ctx.addIssue({
                    code: 'custom',
                    message: 'Invalid document order/opening reference.',
                    path: ['documents'],
                });
    }
    if (Date.parse(m.collection.started_at) > Date.parse(m.collection.finished_at))
        ctx.addIssue({
            code: 'custom',
            message: 'Collection timestamps are reversed.',
            path: ['collection'],
        });
    if (Date.parse(m.collection.finished_at) > Date.now() + 300000)
        ctx.addIssue({
            code: 'custom',
            message: 'Collection timestamp is in the future.',
            path: ['collection'],
        });
    for (const field of ['client_document_id', 'multipart_field']) {
        const ids = m.documents.map((d) => d[field]).filter(Boolean);
        if (new Set(ids).size !== ids.length)
            ctx.addIssue({
                code: 'custom',
                message: 'Duplicate document identifier or part.',
                path: ['documents'],
            });
    }
    if (m.documents.some((d) => d.multipart_field === 'manifest'))
        ctx.addIssue({
            code: 'custom',
            message: 'The manifest field is reserved.',
            path: ['documents'],
        });
    if (new Set(m.reused_evidence.map((e) => e.evidence_ref)).size !==
        m.reused_evidence.length)
        ctx.addIssue({
            code: 'custom',
            message: 'Duplicate reused evidence.',
            path: ['reused_evidence'],
        });
});
export const CollectionProgressInputSchema = z
    .object({
    client_submission_id: z.string().uuid(),
    batch_id: z.string().uuid().nullable(),
    base_submission_id: z.string().uuid().nullable(),
    display_project_number: SourceIdSchema,
    state: z.enum(['selected', 'collecting', 'blocked', 'cancelled']),
    note: ShortText.default(''),
})
    .strict();
export const CollectionProgressSchema = z.object({
    client_submission_id: z.string().uuid(),
    submission_id: z.string().uuid().nullable(),
    batch_id: z.string().uuid().nullable(),
    state: z.enum([
        'selected',
        'collecting',
        'paused',
        'blocked',
        'cancelled',
        'uploading',
        'upload_expired',
        'upload_rejected',
    ]),
    note: z.string(),
    updated_at: z.string(),
    document_count: z.number().nullable(),
});
export const BatchSchema = z
    .object({
    client_batch_id: z.string().uuid(),
    report_id: SourceIdSchema,
    report_start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    report_end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    timezone: z.string().max(100),
    transaction_count: z.number().int().nonnegative(),
    page_count: z.number().int().nonnegative(),
    projects: z
        .array(z.object({
        display_project_number: SourceIdSchema,
        st_project_id: SourceIdSchema.nullable(),
        note: ShortText.optional(),
    }))
        .max(10000),
})
    .strict();
export const CSV_COLUMNS = [
    'Project Number',
    'Status',
    'Grade',
    'Status & Grade Summary',
    'Last Checked',
    'Report Start Date',
    'Report End Date',
    'Job IDs',
    'Pre-Site Form Status',
    'Pre-Site Form URLs',
    'STEP 2 | OPENING WIDTH',
    'STEP 3 | OPENING HEIGHT',
    'STEP 11 | NEW GARAGE DOOR DIMENSIONS',
    'Sold Estimate IDs',
    'Equipment Name & Code',
    'Equipment Description',
    'Purchase Order IDs',
    'Order Confirmation Files',
    'Acknowledgement Files',
    'Measurements vs Estimate',
    'Estimate vs Purchase Order',
    'Acknowledgement vs Order and Estimate',
    'Discrepancies',
    'Missing Documents',
    'Next Action',
    'Project URL',
    'Evidence References',
    'Resolution Details',
];
export const CitationSchema = EvidenceCitationSchema;
export const FindingSchema = z
    .object({
    category: z.string().max(100),
    opening_ref: z.string().max(100).nullable(),
    severity: z.enum(['minor', 'clarification', 'confirmed_defect']),
    field: z.string().max(100),
    message: ShortText,
    evidence: z.array(CitationSchema).min(1).max(30),
    action: ShortText,
    responsible_role: z.string().max(100),
})
    .strict();
export const AnalysisResultSchema = z
    .object({
    review_status: z.enum([
        'Processed',
        'Needs clarification',
        'Waiting on documents',
    ]),
    grade: z.enum(['A', 'B', 'C', 'F']).nullable(),
    summary: ShortText,
    resolution: ShortText,
    findings: z.array(FindingSchema).max(100),
    workflow: WorkflowResultSchema.optional(),
    missing_evidence: z.array(ShortText).max(100),
    csv: z
        .object(Object.fromEntries(CSV_COLUMNS.map((key) => [key, z.string().max(20000)])))
        .strict(),
})
    .strict();
export const WorkflowAnalysisResultSchema = AnalysisResultSchema.extend({
    workflow: WorkflowResultSchema,
});
export const CurrentWorkflowAnalysisResultSchema = AnalysisResultSchema.extend({
    workflow: CurrentWorkflowResultSchema,
});
export const TranscribedPageSchema = z
    .object({
    content: z.string().min(1).max(60000),
    uncertainties: z.array(z.string().max(1000)).max(100),
})
    .strict();
